import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [site, setSite] = useState({});
  const [loading, setLoading] = useState(true);
  const [tcNeedsAcceptance, setTcNeedsAcceptance] = useState(false);

  const loadUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        try { const { data } = await api.get('/site-info'); setSite(data); } catch (e) {}
        setLoading(false);
        return;
      }
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      if (data.site) setSite(data.site);
    } catch (err) {
      localStorage.removeItem('accessToken');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  // Dynamic favicon
  useEffect(() => {
    if (site?.site_favicon_path) {
      const faviconPath = site.site_favicon_path.split('?')[0]; // strip cache bust
      let link = document.querySelector("link[rel*='icon']");
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = faviconPath;
    }
  }, [site]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('accessToken', data.token);
    setUser(data.user);
    if (data.user?.must_accept_tc) setTcNeedsAcceptance(true);
    return data;
  };

  const register = async (formData) => {
    const { data } = await api.post('/auth/register', formData);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    setUser(null);
  };

  const updateUser = (updates) => setUser(prev => ({ ...prev, ...updates }));

  const acceptTc = async () => {
    await api.post('/auth/accept-tc');
    setTcNeedsAcceptance(false);
  };

  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{
      user, site, loading, login, register, logout, updateUser,
      isAdmin, tcNeedsAcceptance, acceptTc, loadUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
